import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Button, TextField, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Paper, CircularProgress, Alert,
    IconButton, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
    Stepper, Step, StepLabel, FormControl, InputLabel, Select, MenuItem,
    OutlinedInput, Checkbox, ListItemText, InputAdornment, Tooltip, Switch,
    FormControlLabel
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Restore as RestoreIcon,
    DirectionsCar as CarIcon,
    Warning as WarningIcon,
} from '@mui/icons-material';
import { carTypesAPI, pricingAPI } from '../services/apiService';

const WIZARD_STEPS = ['Basic Info', 'Can Take Calls For', 'Formula Pricing', 'Route Prices'];

const CarTypeManager = () => {
    const [carTypes, setCarTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [pricingStatus, setPricingStatus] = useState(null);

    // Edit dialog
    const [editOpen, setEditOpen] = useState(false);
    const [editingType, setEditingType] = useState(null);

    // Create wizard
    const [wizardOpen, setWizardOpen] = useState(false);
    const [wizardStep, setWizardStep] = useState(0);
    const [wizardData, setWizardData] = useState({
        name: '',
        sortOrder: 0,
        defaultSeats: 4,
        canTakeCallsFor: [],
        formulaPerMileRate: 0,
        routePrices: [],
    });
    const [routePairs, setRoutePairs] = useState([]);
    const [wizardSaving, setWizardSaving] = useState(false);

    const fetchCarTypes = useCallback(async () => {
        try {
            setLoading(true);
            const data = await carTypesAPI.getAllIncludingInactive();
            setCarTypes(data);
            setError(null);
        } catch (err) {
            setError('Failed to load car types: ' + (err.response?.data?.error || err.message));
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchPricingStatus = useCallback(async () => {
        try {
            const status = await carTypesAPI.checkPricingComplete();
            setPricingStatus(status);
        } catch (err) {
            console.error('Failed to check pricing status:', err);
        }
    }, []);

    useEffect(() => {
        fetchCarTypes();
        fetchPricingStatus();
    }, [fetchCarTypes, fetchPricingStatus]);

    // ========== CREATE WIZARD ==========
    const openWizard = async () => {
        try {
            const pairs = await carTypesAPI.getRoutePairs();
            setRoutePairs(pairs);
            // Get next sort order
            const activeTypes = carTypes.filter(ct => ct.isActive);
            const maxSort = activeTypes.length > 0 ? Math.max(...activeTypes.map(ct => ct.sortOrder)) : 0;
            setWizardData({
                name: '',
                sortOrder: maxSort + 1,
                defaultSeats: 4,
                canTakeCallsFor: [],
                formulaPerMileRate: 0,
                minYear: null,
                routePrices: pairs.map(p => ({
                    originZoneId: p.originZoneId,
                    destinationZoneId: p.destinationZoneId,
                    originName: p.originZoneName,
                    destinationName: p.destinationZoneName,
                    price: 0,
                    ownerCut: 0,
                })),
            });
            setWizardStep(0);
            setWizardOpen(true);
        } catch (err) {
            setError('Failed to load route pairs: ' + (err.response?.data?.error || err.message));
        }
    };

    const handleWizardNext = () => setWizardStep(prev => prev + 1);
    const handleWizardBack = () => setWizardStep(prev => prev - 1);

    const isWizardStepValid = () => {
        switch (wizardStep) {
            case 0: return wizardData.name.trim() !== '' && wizardData.defaultSeats > 0;
            case 1: return true; // canTakeCallsFor can be empty (self will be auto-added server-side)
            case 2: return wizardData.formulaPerMileRate > 0;
            case 3: return wizardData.routePrices.length === 0 || wizardData.routePrices.some(rp => rp.price > 0);
            default: return true;
        }
    };

    const handleCreateSubmit = async () => {
        try {
            setWizardSaving(true);
            await carTypesAPI.create({
                ...wizardData,
                routePrices: wizardData.routePrices.filter(rp => rp.price > 0),
            });
            setWizardOpen(false);
            setSuccess('Car type created successfully');
            setTimeout(() => setSuccess(null), 3000);
            await fetchCarTypes();
            await fetchPricingStatus();
        } catch (err) {
            setError('Failed to create car type: ' + (err.response?.data?.error || err.message));
        } finally {
            setWizardSaving(false);
        }
    };

    // ========== EDIT ==========
    const openEdit = (carType) => {
        setEditingType({
            ...carType,
            canTakeCallsFor: typeof carType.canTakeCallsFor === 'string'
                ? JSON.parse(carType.canTakeCallsFor)
                : carType.canTakeCallsFor || [],
            minYear: carType.minYear ?? null,
        });
        setEditOpen(true);
    };

    const handleEditSave = async () => {
        try {
            const payload = {
                name: editingType.name,
                sortOrder: editingType.sortOrder,
                defaultSeats: editingType.defaultSeats,
                canTakeCallsFor: editingType.canTakeCallsFor,
                minYear: editingType.minYear ?? null,
            };
            await carTypesAPI.update(editingType.id, payload);
            setEditOpen(false);
            setSuccess('Car type updated successfully');
            setTimeout(() => setSuccess(null), 3000);
            await fetchCarTypes();
        } catch (err) {
            setError('Failed to update car type: ' + (err.response?.data?.error || err.message));
        }
    };

    // ========== DEACTIVATE / REACTIVATE ==========
    const handleDeactivate = async (id) => {
        if (!window.confirm('Deactivate this car type? It will be hidden from all dropdowns but historical data is preserved.')) return;
        try {
            await carTypesAPI.deactivate(id);
            setSuccess('Car type deactivated');
            setTimeout(() => setSuccess(null), 3000);
            await fetchCarTypes();
            await fetchPricingStatus();
        } catch (err) {
            setError('Failed to deactivate: ' + (err.response?.data?.error || err.message));
        }
    };

    const handleReactivate = async (id) => {
        try {
            await carTypesAPI.reactivate(id);
            setSuccess('Car type reactivated');
            setTimeout(() => setSuccess(null), 3000);
            await fetchCarTypes();
            await fetchPricingStatus();
        } catch (err) {
            setError('Failed to reactivate: ' + (err.response?.data?.error || err.message));
        }
    };

    // ========== HELPERS ==========
    const getCarTypeName = (id) => {
        const ct = carTypes.find(c => c.id === id);
        return ct ? ct.name : `Unknown (${id})`;
    };

    const activeCarTypes = carTypes.filter(ct => ct.isActive);

    const parseCanTakeCallsFor = (ct) => {
        try {
            const val = typeof ct.canTakeCallsFor === 'string'
                ? JSON.parse(ct.canTakeCallsFor)
                : ct.canTakeCallsFor;
            return Array.isArray(val) ? val : [];
        } catch { return []; }
    };

    if (loading) {
        return <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>;
    }

    return (
        <Box>
            {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>{success}</Alert>}


            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Box>
                    <Typography variant="h6">Car Types</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Manage vehicle categories. Each car type needs a formula rate and route prices before calls can be created.
                    </Typography>
                </Box>
                <Button variant="contained" startIcon={<AddIcon />} onClick={openWizard}>
                    Add Car Type
                </Button>
            </Box>

            {/* Car types table */}
            <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell align="center">Sort Order</TableCell>
                            <TableCell align="center">Default Seats</TableCell>
                            <TableCell>Can Take Calls For</TableCell>
                            <TableCell align="center">Min Year</TableCell>
                            <TableCell align="center">Status</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {carTypes.sort((a, b) => a.sortOrder - b.sortOrder).map(ct => (
                            <TableRow key={ct.id} sx={{ opacity: ct.isActive ? 1 : 0.5 }}>
                                <TableCell>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <CarIcon fontSize="small" color={ct.isActive ? 'primary' : 'disabled'} />
                                        <Typography variant="body2" fontWeight={500}>{ct.name}</Typography>
                                    </Box>
                                </TableCell>
                                <TableCell align="center">{ct.sortOrder}</TableCell>
                                <TableCell align="center">{ct.defaultSeats}</TableCell>
                                <TableCell>
                                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                                        {parseCanTakeCallsFor(ct).map(id => (
                                            <Chip key={id} label={getCarTypeName(id)} size="small" variant="outlined" />
                                        ))}
                                    </Box>
                                </TableCell>
                                <TableCell align="center">{ct.minYear ?? '—'}</TableCell>
                                <TableCell align="center">
                                    <Chip
                                        label={ct.isActive ? 'Active' : 'Inactive'}
                                        color={ct.isActive ? 'success' : 'default'}
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell align="right">
                                    <Tooltip title="Edit">
                                        <IconButton size="small" onClick={() => openEdit(ct)}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    {ct.isActive ? (
                                        <Tooltip title="Deactivate">
                                            <IconButton size="small" color="error" onClick={() => handleDeactivate(ct.id)}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    ) : (
                                        <Tooltip title="Reactivate">
                                            <IconButton size="small" color="success" onClick={() => handleReactivate(ct.id)}>
                                                <RestoreIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* ========== CREATE WIZARD DIALOG ========== */}
            <Dialog open={wizardOpen} onClose={() => setWizardOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>Create New Car Type</DialogTitle>
                <DialogContent>
                    <Stepper activeStep={wizardStep} sx={{ mb: 3, mt: 1 }}>
                        {WIZARD_STEPS.map(label => (
                            <Step key={label}><StepLabel>{label}</StepLabel></Step>
                        ))}
                    </Stepper>

                    {/* Step 0: Basic Info */}
                    {wizardStep === 0 && (
                        <Box display="flex" flexDirection="column" gap={2}>
                            <TextField
                                label="Name"
                                value={wizardData.name}
                                onChange={e => setWizardData(prev => ({ ...prev, name: e.target.value }))}
                                fullWidth
                                required
                                placeholder="e.g. SUV, 12 Passenger"
                            />
                            <TextField
                                label="Default Seats"
                                type="number"
                                value={wizardData.defaultSeats}
                                onChange={e => setWizardData(prev => ({ ...prev, defaultSeats: parseInt(e.target.value) || 0 }))}
                                fullWidth
                                required
                                inputProps={{ min: 1 }}
                            />
                            <TextField
                                label="Sort Order"
                                type="number"
                                value={wizardData.sortOrder}
                                onChange={e => setWizardData(prev => ({ ...prev, sortOrder: parseInt(e.target.value) || 0 }))}
                                fullWidth
                                helperText="Controls display order in dropdowns. Lower numbers appear first."
                            />
                            <TextField
                                label="Minimum Car Year"
                                type="number"
                                value={wizardData.minYear ?? ''}
                                onChange={e => setWizardData(prev => ({ ...prev, minYear: e.target.value ? parseInt(e.target.value) : null }))}
                                fullWidth
                                inputProps={{ min: 1990, max: 2030 }}
                                helperText="Optional. Drivers must have a car this year or newer (e.g. 2020 for Luxury SUV). Leave blank for no restriction."
                            />
                        </Box>
                    )}

                    {/* Step 1: Can Take Calls For */}
                    {wizardStep === 1 && (
                        <Box>
                            <Typography variant="body2" color="text.secondary" mb={2}>
                                Select which call types this car type can accept. The new car type will
                                automatically be included (can always take its own calls).
                            </Typography>
                            <FormControl fullWidth>
                                <InputLabel>Can Take Calls For</InputLabel>
                                <Select
                                    multiple
                                    value={wizardData.canTakeCallsFor}
                                    onChange={e => setWizardData(prev => ({ ...prev, canTakeCallsFor: e.target.value }))}
                                    input={<OutlinedInput label="Can Take Calls For" />}
                                    renderValue={(selected) => (
                                        <Box display="flex" flexWrap="wrap" gap={0.5}>
                                            {selected.map(id => (
                                                <Chip key={id} label={getCarTypeName(id)} size="small" />
                                            ))}
                                        </Box>
                                    )}
                                >
                                    {activeCarTypes.map(ct => (
                                        <MenuItem key={ct.id} value={ct.id}>
                                            <Checkbox checked={wizardData.canTakeCallsFor.includes(ct.id)} />
                                            <ListItemText primary={ct.name} />
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    )}

                    {/* Step 2: Formula Price */}
                    {wizardStep === 2 && (
                        <Box>
                            <Typography variant="body2" color="text.secondary" mb={2}>
                                Set the per-mile rate for formula-based pricing. Formula: <strong>rate × distance + tolls</strong>.
                            </Typography>
                            <TextField
                                label="Per Mile Rate"
                                type="number"
                                value={wizardData.formulaPerMileRate}
                                onChange={e => setWizardData(prev => ({ ...prev, formulaPerMileRate: parseFloat(e.target.value) || 0 }))}
                                fullWidth
                                required
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                }}
                                inputProps={{ min: 0, step: 0.01 }}
                            />
                        </Box>
                    )}

                    {/* Step 3: Route Prices */}
                    {wizardStep === 3 && (
                        <Box>

                            {wizardData.routePrices.length === 0 ? (
                                <Alert severity="info">No routes configured yet. You can skip this step.</Alert>
                            ) : (
                                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                                    <Table size="small" stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Origin</TableCell>
                                                <TableCell>Destination</TableCell>
                                                <TableCell align="right">Price ($)</TableCell>
                                                <TableCell align="right">Owner Cut ($)</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {wizardData.routePrices.map((rp, idx) => (
                                                <TableRow key={idx}>
                                                    <TableCell>{rp.originName}</TableCell>
                                                    <TableCell>{rp.destinationName}</TableCell>
                                                    <TableCell align="right">
                                                        <TextField
                                                            size="small"
                                                            type="number"
                                                            value={rp.price}
                                                            onChange={e => {
                                                                const val = parseFloat(e.target.value) || 0;
                                                                setWizardData(prev => ({
                                                                    ...prev,
                                                                    routePrices: prev.routePrices.map((r, i) =>
                                                                        i === idx ? { ...r, price: val } : r
                                                                    ),
                                                                }));
                                                            }}
                                                            inputProps={{ min: 0, step: 0.01 }}
                                                            sx={{ width: 100 }}
                                                        />
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <TextField
                                                            size="small"
                                                            type="number"
                                                            value={rp.ownerCut}
                                                            onChange={e => {
                                                                const val = parseFloat(e.target.value) || 0;
                                                                setWizardData(prev => ({
                                                                    ...prev,
                                                                    routePrices: prev.routePrices.map((r, i) =>
                                                                        i === idx ? { ...r, ownerCut: val } : r
                                                                    ),
                                                                }));
                                                            }}
                                                            inputProps={{ min: 0, step: 0.01 }}
                                                            sx={{ width: 100 }}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setWizardOpen(false)}>Cancel</Button>
                    {wizardStep > 0 && <Button onClick={handleWizardBack}>Back</Button>}
                    {wizardStep < WIZARD_STEPS.length - 1 ? (
                        <Button
                            variant="contained"
                            onClick={handleWizardNext}
                            disabled={!isWizardStepValid()}
                        >
                            Next
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            onClick={handleCreateSubmit}
                            disabled={wizardSaving || !isWizardStepValid()}
                        >
                            {wizardSaving ? <CircularProgress size={20} /> : 'Create Car Type'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* ========== EDIT DIALOG ========== */}
            <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Edit Car Type</DialogTitle>
                <DialogContent>
                    {editingType && (
                        <Box display="flex" flexDirection="column" gap={2} mt={1}>
                            <TextField
                                label="Name"
                                value={editingType.name}
                                onChange={e => setEditingType(prev => ({ ...prev, name: e.target.value }))}
                                fullWidth
                                required
                            />
                            <TextField
                                label="Default Seats"
                                type="number"
                                value={editingType.defaultSeats}
                                onChange={e => setEditingType(prev => ({ ...prev, defaultSeats: parseInt(e.target.value) || 0 }))}
                                fullWidth
                                inputProps={{ min: 1 }}
                            />
                            <TextField
                                label="Sort Order"
                                type="number"
                                value={editingType.sortOrder}
                                onChange={e => setEditingType(prev => ({ ...prev, sortOrder: parseInt(e.target.value) || 0 }))}
                                fullWidth
                            />
                            <FormControl fullWidth>
                                <InputLabel>Can Take Calls For</InputLabel>
                                <Select
                                    multiple
                                    value={editingType.canTakeCallsFor}
                                    onChange={e => setEditingType(prev => ({ ...prev, canTakeCallsFor: e.target.value }))}
                                    input={<OutlinedInput label="Can Take Calls For" />}
                                    renderValue={(selected) => (
                                        <Box display="flex" flexWrap="wrap" gap={0.5}>
                                            {selected.map(id => (
                                                <Chip key={id} label={getCarTypeName(id)} size="small" />
                                            ))}
                                        </Box>
                                    )}
                                >
                                    {activeCarTypes.map(ct => (
                                        <MenuItem key={ct.id} value={ct.id}>
                                            <Checkbox checked={editingType.canTakeCallsFor.includes(ct.id)} />
                                            <ListItemText primary={ct.name} />
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <TextField
                                label="Minimum Car Year"
                                type="number"
                                value={editingType.minYear ?? ''}
                                onChange={e => setEditingType(prev => ({ ...prev, minYear: e.target.value ? parseInt(e.target.value) : null }))}
                                fullWidth
                                inputProps={{ min: 1990, max: 2030 }}
                                helperText="Optional. Drivers must have a car this year or newer to take these rides (e.g. 2020 for Luxury SUV). Leave blank for no restriction."
                            />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleEditSave} disabled={!editingType?.name?.trim()}>
                        Save Changes
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default CarTypeManager;
